import { useCallback } from 'react';
import { useLocation, useNavigate, useRouteError } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorContent } from './error-content';

/**
 * The data router's `errorElement`. Any error thrown by a loader, action or during
 * render of a route below it surfaces here and is mapped through {@link
 * ErrorContent} to one of the six taxonomy shapes. The parent calendar route uses
 * its own boundary so a Detail_Panel failure does not tear down the calendar
 * beside it.
 *
 * ## Recovering without discarding the document
 *
 * This screen used to offer only "Reload", which is why a screen that failed once
 * had to be recovered with a full application boot. The retry below does the two
 * things a reload does that actually matter here, and nothing else:
 *
 * 1. **Resets the cached reads**, so the remounted screen fetches again instead of
 *    re-rendering the very payload that just failed. This is the part a plain
 *    re-navigation cannot do: React Query would hand the remounted screen the same
 *    cache entry and it would throw again immediately.
 * 2. **Re-navigates to the current location**, which is what clears the router's
 *    error state and remounts the route. React Router keeps an `errorElement`
 *    mounted until the next navigation; there is no separate reset API.
 *
 * The session query is deliberately left alone. Resetting it would blank the shell
 * to its skeleton and re-run the `/api/me` bootstrap for a failure that had
 * nothing to do with the session.
 */
export function RouteErrorBoundary(): React.JSX.Element {
  const error = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const retry = useCallback((): void => {
    void queryClient.resetQueries({
      predicate: (query) => query.queryKey[0] !== 'me',
    });
    void navigate(`${location.pathname}${location.search}`, { replace: true });
  }, [queryClient, navigate, location.pathname, location.search]);

  return <ErrorContent error={error} boundary="route-boundary" onRetry={retry} />;
}
